import React from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  CollapsibleMenuItem,
  Logo,
  MenuItem,
  Sidebar,
  TopNav,
} from '@tempmee/react';
import {
  OutlineDeviceTabletIcon,
  OutlineExternalLinkIcon,
  OutlineLogoutIcon,
} from '@tempmee/react-icons';
import { Constant } from '../../../ApiRules/ApiRules';
import { logout } from '../../../actions/authActions';
import { getShouldEnableBestInDental, getShouldEnablePermanentJobV2 } from '../../../posthog';
import { BarMenu } from './BarMenu';
import { buildNavSections, isRouteActive, isSectionActive } from './navItems';
import { useAppBootstrap } from '../../../hooks/useAppBootstrap';
import Styles from './AppShell.module.scss';

/**
 * The rail's width, in px.
 *
 * `Sidebar` takes this as a prop, and the brand block above it is sized from
 * `$rail-width` in AppShell.module.scss. The two have to match or the chrome's
 * inner edge kinks halfway down — keep them in step.
 */
const RAIL_WIDTH = 260;

/** Trailing "opens externally" marker. Inline-sized because MenuItem's
 * `[&_svg]:size-6` icon rule would otherwise inflate this small indicator
 * to 24px. */
const ExternalMark = () => (
  <OutlineExternalLinkIcon
    aria-hidden
    className="shrink-0 text-text-secondary"
    style={{ width: 14, height: 14 }}
  />
);

/**
 * The Admin Panel v3 shell, following the design system's Examples/Admin Shell
 * story: one dark themed surface — `TopNav color="dark"` over a matching
 * `Sidebar color="dark"` — with text-only nav rows.
 *
 * Two deliberate placements, both from that design:
 *   * App Version and the signed-in account live in the app bar, not the rail.
 *     The rail is for navigating the product; neither of those is navigation.
 *   * Rail rows carry no icons, and nested children are set apart by
 *     indentation alone.
 *
 * The shell is a plain flex column rather than `DashboardLayout`. That is what
 * the story does, and it sidesteps two problems the block gave us: its sidebar
 * relies on a container query that Bootstrap's
 * `.hidden { display: none !important }` defeats, and `main` here can carry
 * `min-w-0` so a wide legacy page shrinks instead of pushing the whole layout
 * into horizontal overflow.
 *
 * Rail rows are real anchors (`href` built with `history.createHref`, so it
 * carries the router's basename) so the browser offers its native "open in new
 * tab" affordances — cmd/ctrl/middle-click, the context
 * menu, and URL-on-hover — none of which a `<button>` can give. A plain
 * left-click is intercepted and turned back into a `history.push` so ordinary
 * navigation stays a SPA transition, and that push still carries the item's
 * location `state`; only a modified click (which opens a fresh document) drops
 * it, and the routes that set `state` don't read it back anyway.
 */
export const AppShell = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const adminUser = useSelector((state) => state.users?.adminUser);
  const appVersions = useSelector((state) => state.users?.allMetadata?.appVersions);

  // The shell replaced Dashboard as the always-mounted chrome, so it also
  // inherited Dashboard's job of loading the shared reference data.
  useAppBootstrap();

  const sections = buildNavSections({
    isPermanentJobV2Enabled: getShouldEnablePermanentJobV2(),
    isBestInDentalEnabled: getShouldEnableBestInDental(),
  });

  // A modified click (cmd/ctrl/shift/alt, or middle-button) is the browser's
  // "open in a new tab/window" gesture — let the anchor's real `href` handle it
  // natively. Everything else becomes an in-app SPA transition that still
  // carries the row's location `state`.
  const go = (item) => (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
    e.preventDefault();
    history.push({ pathname: item.to, state: item.state });
  };

  // The logo doubles as a "home" affordance — the shell has no other route back
  // to the landing page. `/` renders MapContainer (see App.js), the app's home.
  const goHome = () => history.push('/');
  const onLogoKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goHome();
    }
  };

  const renderLeaf = (item) => (
    <MenuItem
      key={item.id}
      // `createHref` rather than `item.to` so the href carries the router's
      // basename. The app is not always served from the domain root — preview
      // builds sit under `/admin-portlal/pr-<n>` — and `push` applies that
      // basename while a raw href, resolved against the origin, would not.
      href={history.createHref({ pathname: item.to })}
      selected={isRouteActive(location.pathname, item.to)}
      onClick={go(item)}
    >
      {item.label}
    </MenuItem>
  );

  const barMenus = (
    <div className="flex items-center gap-1">
      <BarMenu
        label="App Version"
        icon={<OutlineDeviceTabletIcon aria-hidden className="size-5" />}
      >
        {/* The row is a link to the store either way, which the trailing mark
            already says — so with no version loaded the platform name is the
            whole label rather than a sentence that overflows the popover. */}
        <MenuItem href={Constant.IOS_APP_URL} target="_blank" rel="noopener noreferrer">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            {appVersions?.ios ? `iOS ${appVersions.ios}` : 'iOS'}
            <ExternalMark />
          </span>
        </MenuItem>
        <MenuItem href={Constant.ANDROID_APP_URL} target="_blank" rel="noopener noreferrer">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            {appVersions?.android ? `Android ${appVersions.android}` : 'Android'}
            <ExternalMark />
          </span>
        </MenuItem>
      </BarMenu>

      <BarMenu label={adminUser?.email_id || 'Account'}>
        <MenuItem icon={<OutlineLogoutIcon />} onClick={() => dispatch(logout())}>
          Log out
        </MenuItem>
      </BarMenu>
    </div>
  );

  return (
    // Three segments:
    //
    //   [ brand ][ bar    ]
    //   [ rail  ][ content ]
    //
    // The brand block is the rail's width, so it and the rail below it read as
    // one continuous dark column while the bar keeps its own row beside them.
    // That is what stops the bar's shadow ever crossing the rail: the two never
    // share an edge.
    //
    // Each piece casts along one axis only — the bar downward, the rail
    // rightward — and the brand outranks both, so no elevation lands on the
    // chrome's inner corner. See AppShell.module.scss for the measurements
    // behind those shadows.
    <div className="ds-root flex h-screen flex-col overflow-hidden bg-background">
      <div className="flex shrink-0">
        {/* h-16 matches the bar beside it, so the wordmark and the account menu
            share a baseline. The wordmark is the shell's route home to the
            landing page, so it's a button (role + keyboard) rather than decoration. */}
        <div
          className={`relative flex h-16 shrink-0 items-center bg-primary-contrast px-4 ${Styles.brand}`}
        >
          <Logo
            tone="light"
            appearance="solid"
            mark="full"
            role="button"
            tabIndex={0}
            aria-label="Go to home"
            onClick={goHome}
            onKeyDown={onLogoKeyDown}
            className={`w-[82px] cursor-pointer ${Styles.logo}`}
          />
        </div>

        {/* `logo={false}`, not `null`: TopNav resolves its logo with `??`, so a
            null falls through to the default wordmark. The brand block owns it.
            min-w-0 so a long account email cannot widen the bar past the row. */}
        <TopNav
          className={`relative min-w-0 flex-1 ${Styles.bar}`}
          color="dark"
          logo={false}
          endSlot={barMenus}
        />
      </div>

      <div className="flex min-h-0 flex-1">
        {/* size="lg" = 48px top-level rows, 36px nested children. */}
        <Sidebar
          className={`relative z-10 shrink-0 ${Styles.rail}`}
          color="dark"
          size="lg"
          width={RAIL_WIDTH}
        >
          {/* Sidebar.Content owns the overflow and the DS scrollbar skin. */}
          <Sidebar.Content className="pt-2">
            {sections.map((section) =>
              (section.children ? (
                <CollapsibleMenuItem
                  key={section.id}
                  label={section.label}
                  defaultOpen={isSectionActive(location.pathname, section)}
                >
                  {section.children.map(renderLeaf)}
                </CollapsibleMenuItem>
              ) : (
                renderLeaf(section)
              )),
            )}
          </Sidebar.Content>
        </Sidebar>

        {/* min-w-0 lets a wide legacy page shrink instead of forcing the whole
            layout into horizontal overflow — flex items default to
            min-width:auto, which refuses to go below their content width. */}
        {/* No zoom here. This used to carry `zoom: 0.95` to buy width back for
            legacy full-window layouts, but a fractional zoom puts every box on
            a half-pixel — a 32px DS control measured 30.39px and its glyphs
            rasterised visibly off-centre. Pages own their own width instead. */}
        <main className="min-w-0 flex-1 overflow-auto bg-background">{children}</main>
      </div>
    </div>
  );
};

AppShell.propTypes = {
  children: PropTypes.node,
};

AppShell.defaultProps = { children: null };

export default AppShell;
